import React, { SelectHTMLAttributes } from 'react';
import styled from 'styled-components';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  $hasError?: boolean;
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
}

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 0.75rem 0;
  padding: 0;
`;

const StyledSelect = styled.select<{ $hasError?: boolean }>`
  padding: 10px 12px;
  border-radius: 4px;
  border: 1px solid ${({ theme, $hasError }) => 
    $hasError ? theme.colors.error : theme.colors.border};
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  
  /* Style personnalisé pour la flèche du select */
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  padding-right: 36px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) => 
      $hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme, $hasError }) => 
      $hasError ? `${theme.colors.error}30` : `${theme.colors.primary}30`};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.disabledBackground};
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  /* Style pour les options */
  option {
    background-color: ${({ theme }) => theme.colors.inputBackground};
    color: ${({ theme }) => theme.colors.text};
    padding: 8px;
  }
  
  /* Style pour le placeholder */
  &:invalid {
    color: ${({ theme }) => theme.colors.textLight};
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

export const Select: React.FC<SelectProps> = ({ 
  $hasError, 
  label, 
  error, 
  options, 
  onChange,
  placeholder,
  value,
  ...props 
}) => {
  return (
    <SelectContainer>
      {label && <Label>{label}</Label>}
      <StyledSelect 
        $hasError={$hasError || !!error} 
        onChange={onChange}
        value={value}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </StyledSelect>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </SelectContainer>
  );
};

export default Select;
